import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  type Locale,
} from 'date-fns';
import { enUS, es, ru } from 'date-fns/locale';
import './DateSelect.scss';

const LOCALE_MAP: Record<string, Locale> = { ru, en: enUS, es };

interface DateSelectProps {
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  minYear?: number;
  maxYear?: number;
  id?: string;
  className?: string;
  error?: boolean;
}

const toYmd = (d: Date) => format(d, 'yyyy-MM-dd');

type PanelPos = { top: number; left: number; width: number; mobile: boolean };

export const DateSelect: React.FC<DateSelectProps> = ({
  value,
  onChange,
  minYear = 1900,
  maxYear = new Date().getFullYear(),
  id = 'birthDate',
  className = '',
  error = false,
}) => {
  const { t, i18n } = useTranslation();
  const locale = LOCALE_MAP[i18n.language?.slice(0, 2) || 'ru'] || ru;
  const reactId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<PanelPos | null>(null);

  const selected = useMemo(() => {
    if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      if (!Number.isNaN(date.getTime())) return date;
    }
    return null;
  }, [value]);

  const minDate = useMemo(() => new Date(minYear, 0, 1), [minYear]);
  const maxDate = useMemo(() => new Date(maxYear, 11, 31), [maxYear]);

  const [viewMonth, setViewMonth] = useState<Date>(() => {
    if (selected) return startOfMonth(selected);
    return startOfMonth(new Date(Math.min(Math.max(1990, minYear), maxYear), 0, 1));
  });

  useEffect(() => {
    if (selected) setViewMonth(startOfMonth(selected));
  }, [selected]);

  const updatePosition = () => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mobile = window.innerWidth <= 640;
    if (mobile) {
      setPos({ top: 0, left: 0, width: window.innerWidth, mobile: true });
      return;
    }
    const panelWidth = Math.max(rect.width, 300);
    const left = Math.min(Math.max(12, rect.left), window.innerWidth - panelWidth - 12);
    const spaceBelow = window.innerHeight - rect.bottom;
    const preferBelow = spaceBelow > 340 || spaceBelow > rect.top;
    const top = preferBelow ? rect.bottom + 8 : Math.max(12, rect.top - 8 - 340);
    setPos({ top, left, width: panelWidth, mobile: false });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePosition();
    const onWin = () => updatePosition();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    if (window.innerWidth <= 640) document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const years = useMemo(() => {
    const list: number[] = [];
    for (let y = maxYear; y >= minYear; y--) list.push(y);
    return list;
  }, [minYear, maxYear]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [viewMonth]);

  const weekdayLabels = useMemo(() => {
    const monday = startOfWeek(new Date(2024, 0, 1), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return format(d, 'EEEEE', { locale });
    });
  }, [locale]);

  const label = selected
    ? format(selected, 'd MMM yyyy', { locale })
    : t('home.form.datePlaceholder');

  const canPrev = isAfter(startOfMonth(viewMonth), startOfMonth(minDate));
  const canNext = isBefore(startOfMonth(viewMonth), startOfMonth(maxDate));

  const pickDay = (day: Date) => {
    if (isBefore(day, minDate) || isAfter(day, maxDate)) return;
    onChange(toYmd(day));
    setOpen(false);
  };

  const panelId = `${id || 'birthDate'}-panel-${reactId}`;

  const panel =
    open &&
    pos &&
    createPortal(
      <>
        <button
          type="button"
          className="date-select__backdrop"
          aria-label="Close"
          onClick={() => setOpen(false)}
        />
        <div
          ref={panelRef}
          className={`date-select__panel ${pos.mobile ? 'date-select__panel--mobile' : ''}`}
          id={panelId}
          role="dialog"
          aria-label={t('home.form.birthDate')}
          style={
            pos.mobile
              ? undefined
              : { top: pos.top, left: pos.left, width: pos.width, position: 'fixed' }
          }
        >
          <div className="date-select__toolbar">
            <button
              type="button"
              className="date-select__nav"
              disabled={!canPrev}
              aria-label="Previous month"
              onClick={() => canPrev && setViewMonth((m) => addMonths(m, -1))}
            >
              ‹
            </button>
            <div className="date-select__month-year">
              <span className="date-select__month-label">{format(viewMonth, 'LLLL', { locale })}</span>
              <select
                className="date-select__year-select"
                value={viewMonth.getFullYear()}
                aria-label="Year"
                onChange={(e) => {
                  const y = Number(e.target.value);
                  setViewMonth(new Date(y, viewMonth.getMonth(), 1));
                }}
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="date-select__nav"
              disabled={!canNext}
              aria-label="Next month"
              onClick={() => canNext && setViewMonth((m) => addMonths(m, 1))}
            >
              ›
            </button>
          </div>

          <div className="date-select__weekdays">
            {weekdayLabels.map((w, i) => (
              <span key={`${w}-${i}`}>{w}</span>
            ))}
          </div>

          <div className="date-select__grid">
            {days.map((day) => {
              const inMonth = isSameMonth(day, viewMonth);
              const isSelected = selected ? isSameDay(day, selected) : false;
              const disabled = isBefore(day, minDate) || isAfter(day, maxDate);
              return (
                <button
                  key={toYmd(day)}
                  type="button"
                  disabled={disabled}
                  className={[
                    'date-select__day',
                    inMonth ? '' : 'date-select__day--muted',
                    isSelected ? 'date-select__day--selected' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => pickDay(day)}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>
        </div>
      </>,
      document.body
    );

  return (
    <div
      ref={rootRef}
      className={`date-select ${className} ${error ? 'date-select--error' : ''} ${open ? 'date-select--open' : ''}`}
    >
      <button
        type="button"
        id={id}
        className="date-select__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`date-select__value ${selected ? '' : 'date-select__value--placeholder'}`}>
          {label}
        </span>
        <span className="date-select__icon" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M3 10h18" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {panel}
    </div>
  );
};

export default DateSelect;
